
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { CampaignDetailMetrics as CampaignMetricsType } from "@/hooks/useMetaMetrics";
import { MetricsTable, CampaignHealthScore } from "@/components/dashboard/MetaPerformanceRating";

interface CampaignDetailMetricsProps {
  campaignDetails: CampaignMetricsType;
  isLoading?: boolean;
}

export const CampaignDetailMetrics = ({ 
  campaignDetails, 
  isLoading = false 
}: CampaignDetailMetricsProps) => {
  if (isLoading) {
    return (
      <Card className="bg-[#0c0c0c] border border-gray-800/30">
        <CardHeader>
          <CardTitle className="text-lg">Kampagnen Details werden geladen...</CardTitle>
        </CardHeader>
      </Card>
    );
  }

  if (!campaignDetails) {
    return null;
  }

  const formatNumber = (value: number, decimals = 2) => {
    return typeof value === 'number' 
      ? value.toLocaleString('de-DE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
      : value;
  };
  
  const formatPercentage = (value: number) => {
    return typeof value === 'number'
      ? `${(value * 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`
      : value;
  };
  
  const formatCurrency = (value: number) => {
    return typeof value === 'number'
      ? `${value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`
      : value;
  };

  const metricGroups = [
    {
      title: "Allgemein",
      metrics: [
        { label: "Kampagne", value: campaignDetails.name },
        { label: "Status", value: campaignDetails.status },
        { label: "Berichtszeitraum", value: `${campaignDetails.reportStart} bis ${campaignDetails.reportEnd}` },
        { label: "Attributionseinstellung", value: campaignDetails.attributionSetting },
      ]
    },
    {
      title: "Ergebnisse & Kosten",
      metrics: [
        { label: "Ergebnistyp", value: campaignDetails.resultType },
        { label: "Ergebnisse", value: formatNumber(campaignDetails.results, 0) },
        { label: "Kosten pro Ergebnis", value: formatCurrency(campaignDetails.costPerResult) },
        { label: "Ausgegebener Betrag", value: formatCurrency(campaignDetails.spentAmount) },
      ]
    },
    {
      title: "Reichweite & Häufigkeit",
      metrics: [
        { label: "Reichweite", value: formatNumber(campaignDetails.reach, 0) },
        { label: "Aufrufe", value: formatNumber(campaignDetails.views, 0) },
        { label: "Frequenz", value: formatNumber(campaignDetails.frequency, 2) },
        { label: "Impressionen", value: formatNumber(campaignDetails.impressions, 0) },
        { label: "CPM", value: formatCurrency(campaignDetails.cpm) },
      ]
    },
    {
      title: "Klicks & Interaktionen",
      metrics: [
        { label: "Link-Klicks", value: formatNumber(campaignDetails.linkClicks, 0) },
        { label: "CPC (Link-Klicks)", value: formatCurrency(campaignDetails.cpc) },
        { label: "CTR (Link-Klickrate)", value: formatPercentage(campaignDetails.ctr) },
        { label: "Klicks (alle)", value: formatNumber(campaignDetails.allClicks, 0) },
        { label: "CTR (alle)", value: formatPercentage(campaignDetails.allCtr) },
        { label: "CPC (alle)", value: formatCurrency(campaignDetails.allCpc) },
      ]
    },
    {
      title: "Einstufungen",
      metrics: [
        { label: "Qualität", value: campaignDetails.qualityRating },
        { label: "Interaktionsrate", value: campaignDetails.engagementRating },
        { label: "Conversion Rate", value: campaignDetails.conversionRating },
      ]
    }
  ];

  // Prepare metrics for performance rating
  const performanceMetrics = {
    cpm: campaignDetails.cpm,
    cpc: campaignDetails.cpc,
    ctr: campaignDetails.ctr * 100, // Convert from decimal to percentage
    conversionRate: (campaignDetails.conversionRate || 0) * 100, // Convert from decimal to percentage
    cpl: campaignDetails.costPerResult, // Using cost per result as CPL
    roas: campaignDetails.roi ? (campaignDetails.roi / 100) + 1 : undefined, // Convert ROI to ROAS format
    frequency: campaignDetails.frequency,
    adRelevance: campaignDetails.qualityRating === "UNKNOWN" 
      ? "Niedrig / Negativ" 
      : campaignDetails.qualityRating === "AVERAGE" 
        ? "Neutral / Gemischt" 
        : "Hoch / Positiv"
  };

  return (
    <div className="grid gap-6">
      <Card className="bg-[#0c0c0c] border border-gray-800/30">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Kampagnen Performance</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-6">
            {/* Performance health score */}
            <div className="md:w-1/3">
              <CampaignHealthScore metrics={performanceMetrics} />
            </div>
            
            {/* Performance indicators */}
            <div className="md:w-2/3">
              <h3 className="text-md font-medium text-gray-300 mb-4">Performance Indikatoren</h3>
              <MetricsTable metrics={performanceMetrics} />
            </div>
          </div>
        </CardContent>
      </Card>
      
      <Card className="bg-[#0c0c0c] border border-gray-800/30">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Detaillierte Kampagnenmetriken</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6">
            {metricGroups.map((group, groupIndex) => (
              <div key={groupIndex} className="space-y-4">
                <h3 className="text-md font-medium text-gray-300">{group.title}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {group.metrics.map((metric, metricIndex) => (
                    <div key={metricIndex} className="flex justify-between border-b border-gray-800/30 pb-2">
                      <span className="text-sm text-gray-400">{metric.label}</span>
                      <span className="text-sm font-medium">{metric.value}</span>
                    </div>
                  ))}
                </div>
                {groupIndex < metricGroups.length - 1 && (
                  <Separator className="bg-gray-800/30 my-4" />
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
